/**
 * useCollapsibleSection — persists collapsed/expanded state of a section
 * across page reloads using localStorage. Each section is keyed by a stable
 * string id; default state is "expanded".
 *
 * Storage key is namespaced under `cp:section:` so collisions with other
 * features are impossible. SSR-safe (gated `typeof window`); private-mode /
 * disabled storage gracefully falls back to in-memory state without throwing.
 */

import { useCallback, useEffect, useState } from 'react';

const KEY_PREFIX = 'cp:section:';

function readStored(id: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${KEY_PREFIX}${id}`);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writeStored(id: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${id}`, value ? '1' : '0');
  } catch {
    /* storage disabled — silently no-op */
  }
}

export interface UseCollapsibleSectionReturn {
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  toggle: () => void;
}

export function useCollapsibleSection(
  id: string,
  defaultExpanded: boolean = true,
): UseCollapsibleSectionReturn {
  const [expanded, setExpandedState] = useState<boolean>(() =>
    readStored(id, defaultExpanded),
  );

  // On id change (rare — section keys are stable), re-read from storage.
  useEffect(() => {
    setExpandedState(readStored(id, defaultExpanded));
  }, [id, defaultExpanded]);

  const setExpanded = useCallback(
    (value: boolean) => {
      setExpandedState(value);
      writeStored(id, value);
    },
    [id],
  );

  const toggle = useCallback(() => {
    setExpandedState((prev) => {
      const next = !prev;
      writeStored(id, next);
      return next;
    });
  }, [id]);

  return { expanded, setExpanded, toggle };
}
